import { Button } from "@/components/ui/button";
import { ErrorText } from "@/components/ui/error";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { loginPath, verifyPath, VITE_API_URL } from "@/constants";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/auth/signup")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const resp = await fetch(`${VITE_API_URL}/auth/email`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: email,
        }),
      });
      if (resp.status >= 400) {
        throw new Error("failed to authorize");
      }

      nav({ to: verifyPath });
    } catch (e) {
      console.error(e);
      setErr(t("common.internalError"));
    }
    setIsSubmitting(false);
  };

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="w-full flex flex-col space-y-3">
        <div className="mx-auto w-full max-w-80 relative bottom-8 flex flex-col gap-6">
          <div className="flex w-full flex-col items-center gap-1">
            <h1 className="w-full text-left text-2xl font-semibold tracking-tight text-primary">
              {t("auth.signup.title")}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {err !== "" && (
              <ErrorText className="w-full text-center">{err}</ErrorText>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-500">
                {t("auth.email")}
              </Label>
              <Input
                type="email"
                name="email"
                placeholder={t("auth.emailPlaceholder")}
                onChange={(e) => {
                  setEmail(e.currentTarget.value);
                  setErr("");
                }}
                className="h-10 rounded-sm w-full bg-white text-sm font-normal placeholder:text-gray-400"
                required
                disabled={isSubmitting}
              />
            </div>

            <Button
              type="submit"
              className="flex h-9 items-center justify-center text-white w-full rounded-sm"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("common.sending") : t("common.continue")}
            </Button>
            <div className="w-full text-xs font-medium text-slate-600">
              <span>
                {t("auth.signup.haveAccount")}{" "}
                <Link className="text-primary" to={loginPath}>
                  {t("auth.signup.login")}
                </Link>
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
